import React from 'react';
import { XIcon, TriangleAlertIcon } from 'lucide-react';

import { cn } from '@epoch-ui/lib/utils';
import { formatDistrolistMember } from 'utils/format';
import { Tooltip, TooltipTrigger, TooltipContent } from '@epoch-ui/components/Tooltip';

import { useInviteGuestsModalContext } from '../hooks/useInviteGuestsModalContext';

interface SelectedGuestsListProps {
  readOnly?: boolean;
}

export const SelectedGuestsList: React.FC<SelectedGuestsListProps> = ({ readOnly }) => {
  const {
    selectedUsers,
    selectedDistrolists,
    selectedCommunities,
    toggleUser,
    toggleDistrolist,
    toggleCommunity
  } = useInviteGuestsModalContext();

  const totalSelections =
    selectedUsers.length + selectedDistrolists.length + selectedCommunities.length;

  if (totalSelections === 0) {
    return (
      <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
        <p className="tw-text-sm tw-text-primary-grey">No guests selected</p>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-gap-3">
      {/* Users */}
      {selectedUsers
        .sort((a, b) => {
          if (a.isInvalid && !b.isInvalid) return -1;
          if (!a.isInvalid && b.isInvalid) return 1;
          return 0;
        })
        .map((user) => {
          const content = (
            <div className="tw-flex !tw-cursor-default tw-items-center tw-justify-between">
              <div
                className={cn(
                  'tw-flex tw-flex-1 tw-items-center tw-gap-2',
                  user.isInvalid && 'tw-text-warning-foreground'
                )}
              >
                {user.isInvalid && <TriangleAlertIcon size={16} />}
                <p className="tw-mb-0 tw-text-sm tw-leading-5">
                  {user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.email}
                </p>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => toggleUser(user)}
                  className="tw-rounded tw-flex tw-size-4 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-primary-grey hover:tw-text-black"
                  aria-label="Remove guest"
                >
                  <XIcon className="tw-size-4" />
                </button>
              )}
            </div>
          );

          if (user.isInvalid) {
            return (
              <Tooltip key={user.id || user.email}>
                <TooltipTrigger asChild>{content}</TooltipTrigger>
                <TooltipContent className="tw-max-w-64">
                  This email can&apos;t be added because we couldn&apos;t find it in your directory.
                </TooltipContent>
              </Tooltip>
            );
          }

          return <React.Fragment key={user.id || user.email}>{content}</React.Fragment>;
        })}

      {/* Distrolists */}
      {selectedDistrolists.map((distrolist) => (
        <div key={distrolist.id} className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex-1">
            <p className="tw-mb-0 tw-text-sm tw-leading-5">{distrolist.name}</p>
            <p className="tw-mb-0 tw-text-xs tw-leading-4 tw-text-primary-grey">
              {formatDistrolistMember(distrolist.memberCount || 0)}
            </p>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => toggleDistrolist(distrolist)}
              className="tw-rounded tw-flex tw-size-4 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-primary-grey hover:tw-text-black"
              aria-label="Remove distrolist"
            >
              <XIcon className="tw-size-4" />
            </button>
          )}
        </div>
      ))}

      {/* Communities */}
      {selectedCommunities.map((community) => (
        <div key={community.id} className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex-1">
            <p className="tw-mb-0 tw-text-sm tw-leading-5">{community.name}</p>
            <p className="tw-mb-0 tw-text-xs tw-leading-4 tw-text-primary-grey">
              {formatDistrolistMember(community.memberCount || 0)}
            </p>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => toggleCommunity(community)}
              className="tw-rounded tw-flex tw-size-4 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-primary-grey hover:tw-text-black"
              aria-label="Remove community"
            >
              <XIcon className="tw-size-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};
